import { type ClassDetailsForm } from '~entities/classes/api/create-class';
import {
  buildClassDetailsFormFromClass,
  defaultCopyTitle,
  offeringImageUrl,
} from '~entities/classes/helpers/build-class-details-form';
import type { ClassDetails } from '~entities/classes/modal/class';

import {
  type CopyOfferingFieldOverrides,
  toApiDate,
} from './copy-offering-edit-values';

export type CopyOfferingSource = {
  id: string;
  title: string;
};

export type CopyOfferingCreateInput = {
  file?: string;
  fileType?: 'url';
  classDetails: ClassDetailsForm;
};

export type CopyOfferingFailure = {
  id: string;
  title: string;
  reason: string;
};

export type CopyOfferingsResult = {
  copied: number;
  failed: number;
  failures: CopyOfferingFailure[];
};

export type { CopyOfferingFieldOverrides };

export function copyOfferingDialogCopy(count: number): {
  title: string;
  description: string;
  confirm: string;
} {
  if (count <= 1) {
    return {
      title: 'Confirm copy',
      description:
        'This creates a hidden copy with your edited settings. Bookings and invoices are not copied.',
      confirm: 'Copy offering',
    };
  }
  return {
    title: `Confirm copy of ${count} offerings`,
    description: `Copy ${count} offerings with your edited settings? Each copy is hidden until you unhide it. Bookings and invoices are not copied.`,
    confirm: `Copy ${count} offerings`,
  };
}

function applyOverrides(
  classDetails: ClassDetailsForm,
  override: CopyOfferingFieldOverrides,
): ClassDetailsForm {
  return {
    ...classDetails,
    title: override.title,
    startDate: toApiDate(override.startDate),
    endDate: toApiDate(override.endDate),
    registrationStartDate: override.registrationStartDate
      ? toApiDate(override.registrationStartDate)
      : '',
    tuitionFee: override.tuitionFee,
    costumeFee: override.costumeFee,
    registrationFeeAmount: override.registrationFeeAmount,
  };
}

export async function copyOfferings(params: {
  sources: CopyOfferingSource[];
  customTitle?: string;
  overridesById?: Record<string, CopyOfferingFieldOverrides>;
  fetchOffering: (id: string) => Promise<ClassDetails>;
  createOffering: (input: CopyOfferingCreateInput) => Promise<unknown>;
}): Promise<CopyOfferingsResult> {
  let copied = 0;
  const failures: CopyOfferingFailure[] = [];

  for (const source of params.sources) {
    try {
      const offering = await params.fetchOffering(source.id);
      const override = params.overridesById?.[source.id];
      const title =
        override?.title.trim() ||
        (params.sources.length === 1
          ? params.customTitle?.trim() || defaultCopyTitle(offering.title)
          : defaultCopyTitle(offering.title));
      let classDetails = buildClassDetailsFormFromClass(offering, title, {
        hideOverride: true,
      });
      if (override) {
        classDetails = applyOverrides(classDetails, override);
      }
      const imageUrl = offeringImageUrl(offering);
      await params.createOffering(
        imageUrl
          ? { file: imageUrl, fileType: 'url', classDetails }
          : { classDetails },
      );
      copied += 1;
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      failures.push({
        id: source.id,
        title: source.title,
        reason,
      });
      console.error(
        `Failed to copy offering ${source.id} (${source.title}):`,
        error,
      );
    }
  }

  return { copied, failed: failures.length, failures };
}

export function copyOfferingsToastMessage(result: CopyOfferingsResult): string {
  if (result.failed === 0) {
    return result.copied === 1
      ? 'Offering copied.'
      : `Copied ${result.copied} offerings.`;
  }
  const failedNames = result.failures
    .map((failure) => failure.title || failure.id)
    .join(', ');
  const prefix =
    result.copied === 0
      ? 'Failed to copy offering.'
      : `Copied ${result.copied} of ${result.copied + result.failed}.`;
  return `${prefix} Failed: ${failedNames}.`;
}
